import { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { styles } from "../styles";
import SignatureInput from "./SignatureInput";
import {
  exportPdf,
  loadImageAsDataUrl,
  printPdf,
  shouldShowPdfChooser,
} from "../utils/pdfExport";
import PdfActionModal from "../components/PdfActionModal";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Phase 4.2d — review & dual sign-off for a service ticket.
 *
 * Shows a CUSTOMER-FACING review (materials listed WITHOUT cost, hours by
 * employee + dates), then captures a drawn signature from the contractor and
 * from the facility representative. Applying both locks the ticket to
 * status "signed" (immutable, enforced by firestore.rules).
 */
export default function TicketSignOff({
  ticket,
  job,
  equipmentList,
  actions,
  canSign,
  customer,
  location,
  customerName,
  locationName,
  logoUrl = "",
  orgName = "",
  orgAddress = {},
  // Admin/manager-only: enables the "Export Itemized Copy" button on
  // signed tickets. Shows the same work-order layout with an itemized
  // materials table (unit cost, total, source) — for office records.
  canViewCosts = false,
}) {
  const [open, setOpen] = useState(false);
  const [contractorName, setContractorName] = useState("");
  const [contractorSig, setContractorSig] = useState("");
  const [custName, setCustName] = useState("");
  const [custTitle, setCustTitle] = useState("");
  const [custSig, setCustSig] = useState("");
  const [recipients, setRecipients] = useState("");
  const [saving, setSaving] = useState(false);

  const signed = ticket.status === "signed";

  const eqNames = (ticket.equipmentIds || [])
    .map((id) => {
      const eq = equipmentList.find((e) => e.id === id);
      return eq ? `${eq.type}${eq.subType ? ` (${eq.subType})` : ""}` : null;
    })
    .filter(Boolean);

  const [pdfPending, setPdfPending] = useState(null);
  const handlePdfPrint = () => {
    if (!pdfPending) return;
    printPdf(pdfPending.doc);
    setPdfPending(null);
  };
  const handlePdfSave = async () => {
    if (!pdfPending) return;
    await exportPdf(pdfPending.doc, pdfPending.filename);
    setPdfPending(null);
  };

  // Signed work order PDF. Two variants:
  //   includeCosts = false (default, customer copy) — materials list
  //     name + qty only, no prices.
  //   includeCosts = true (office record, admin/manager only) — adds
  //     unit cost, line total, source (PO #, "CC", or blank for stock)
  //     and a Materials Total row.
  const printSignedCopy = async (includeCosts = false) => {
    const recips = (ticket.sentTo || []).filter(Boolean);
    const jobNumber = job?.jobNumber || "";
    const addrLineList = (addr = {}) => {
      const lines = [];
      if (addr.street) lines.push(addr.street);
      const cityLine = [addr.city, addr.state, addr.zip]
        .filter(Boolean)
        .join(", ");
      if (cityLine) lines.push(cityLine);
      if (addr.phone) lines.push(addr.phone);
      return lines;
    };

    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const PAGE_W = doc.internal.pageSize.getWidth();
    const PAGE_H = doc.internal.pageSize.getHeight();
    const MARGIN = 40;
    const CONTENT_W = PAGE_W - MARGIN * 2;

    // --- Header ---
    const logoData = await loadImageAsDataUrl(logoUrl);
    let titleX = MARGIN;
    if (logoData) {
      try {
        doc.addImage(logoData, "AUTO", MARGIN, MARGIN, 80, 60);
        titleX = MARGIN + 90;
      } catch (err) {
        console.warn("logo addImage failed:", err);
      }
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(34, 34, 34);
    doc.text(
      includeCosts ? "Service Work Order — Office Copy" : "Service Work Order",
      titleX,
      MARGIN + 18
    );

    let headerY = MARGIN + 18;
    if (orgName) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(102, 102, 102);
      headerY += 16;
      doc.text(orgName, titleX, headerY);
    }
    doc.setFontSize(10);
    doc.setTextColor(85, 85, 85);
    for (const line of addrLineList(orgAddress)) {
      headerY += 12;
      doc.text(line, titleX, headerY);
    }
    headerY = Math.max(headerY, MARGIN + 60);

    // --- Doc meta ---
    let y = headerY + 28;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(26, 95, 180);
    doc.text(
      `Job #${jobNumber || "—"}${job?.title ? ` — ${job.title}` : ""}`,
      MARGIN,
      y
    );
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(34, 34, 34);
    const dateStr = `Date: ${ticket.workDate || ""}`;
    doc.text(dateStr, PAGE_W - MARGIN - doc.getTextWidth(dateStr), y);

    // --- Customer / Service Location block ---
    y += 22;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Customer / Service Location", MARGIN, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.text(customer?.name || customerName || "—", MARGIN, y);
    y += 12;
    doc.text(location?.name || locationName || "—", MARGIN, y);
    y += 12;
    const locLines = addrLineList({
      street: location?.street,
      city: location?.city,
      state: location?.state,
      zip: location?.zip,
    });
    for (const line of locLines) {
      doc.text(line, MARGIN, y);
      y += 12;
    }
    y += 6;

    // --- Work performed + equipment ---
    if (ticket.summary) {
      doc.setFont("helvetica", "bold");
      doc.text("Work performed:", MARGIN, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      const sumLines = doc.splitTextToSize(ticket.summary, CONTENT_W);
      doc.text(sumLines, MARGIN, y);
      y += sumLines.length * 13 + 4;
    }
    if (eqNames.length) {
      doc.setFont("helvetica", "bold");
      doc.text("Equipment:", MARGIN, y);
      doc.setFont("helvetica", "normal");
      const eqText = eqNames.join(", ");
      const eqLines = doc.splitTextToSize(eqText, CONTENT_W - 80);
      doc.text(eqLines, MARGIN + 80, y);
      y += eqLines.length * 13 + 6;
    }

    // --- Labor table ---
    const ptLabel = (pt) =>
      pt === "overtime" ? "OT" : pt === "double" ? "DT" : "ST";
    const laborBody = (ticket.labor || []).map((l) => [
      l.employeeName || "—",
      `${num(l.hours)} hr`,
      ptLabel(l.payType),
      l.date || "",
    ]);
    if (laborBody.length === 0) laborBody.push(["None", "", "", ""]);
    autoTable(doc, {
      startY: y + 4,
      head: [["Labor — Employee", "Hours", "Type", "Date"]],
      body: laborBody,
      theme: "plain",
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 11, cellPadding: 5 },
      headStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 34,
        lineWidth: { bottom: 0.75 },
        lineColor: 200,
      },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 60 },
        2: { cellWidth: 50 },
        3: { cellWidth: 100 },
      },
    });
    y = doc.lastAutoTable.finalY + 8;

    // --- Materials table ---
    const money = (n) =>
      n.toLocaleString("en-US", { style: "currency", currency: "USD" });
    const sourceLabel = (m) => {
      if (m.source === "po") return m.poId ? "PO" : "PO (unlinked)";
      if (m.source === "credit_card") return "CC";
      return ""; // stock or unset
    };
    const matLines = (ticket.materials || []);
    let matBody;
    let matHead;
    let matColStyles;
    if (includeCosts) {
      matBody = matLines.map((m) => [
        `${m.name || "—"}${m.partNumber ? ` (#${m.partNumber})` : ""}`,
        sourceLabel(m),
        `${num(m.qty)}`,
        money(num(m.unitCost)),
        money(num(m.qty) * num(m.unitCost)),
      ]);
      if (matBody.length === 0) matBody.push(["None", "", "", "", ""]);
      const matTotal = matLines.reduce(
        (s, m) => s + num(m.qty) * num(m.unitCost),
        0
      );
      matHead = [["Materials", "Source", "Qty", "Unit cost", "Total"]];
      matColStyles = {
        0: { cellWidth: "auto" },
        // Center-align the Source column so the short labels (PO / CC)
        // sit under the "Source" header instead of hugging the left edge
        // next to the description.
        1: { cellWidth: 60, halign: "center" },
        2: { cellWidth: 50, halign: "right" },
        3: { cellWidth: 80, halign: "right" },
        4: { cellWidth: 80, halign: "right" },
      };
      autoTable(doc, {
        startY: y,
        head: matHead,
        body: matBody,
        foot: [
          [
            { content: "Materials total", colSpan: 4, styles: { fontStyle: "bold" } },
            { content: money(matTotal), styles: { fontStyle: "bold", halign: "right" } },
          ],
        ],
        theme: "plain",
        margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 11, cellPadding: 5 },
        headStyles: {
          fontStyle: "bold",
          fillColor: false,
          textColor: 34,
          lineWidth: { bottom: 0.75 },
          lineColor: 200,
        },
        columnStyles: matColStyles,
      });
    } else {
      matBody = matLines.map((m) => [
        `${m.name || "—"}${m.partNumber ? ` (#${m.partNumber})` : ""}`,
        `${num(m.qty)}`,
      ]);
      if (matBody.length === 0) matBody.push(["None", ""]);
      autoTable(doc, {
        startY: y,
        head: [["Materials", "Qty"]],
        body: matBody,
        theme: "plain",
        margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 11, cellPadding: 5 },
        headStyles: {
          fontStyle: "bold",
          fillColor: false,
          textColor: 34,
          lineWidth: { bottom: 0.75 },
          lineColor: 200,
        },
        columnStyles: {
          0: { cellWidth: "auto" },
          1: { cellWidth: 80 },
        },
      });
    }
    y = doc.lastAutoTable.finalY + 14;

    // --- Signature blocks ---
    const drawSig = (s, label, startY) => {
      let cur = startY;
      if (!s) return cur;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(34, 34, 34);
      const head = `${label}: ${s.name || ""}${
        s.title ? `, ${s.title}` : ""
      }${s.signedAt ? ` — ${new Date(s.signedAt).toLocaleString()}` : ""}`;
      const headLines = doc.splitTextToSize(head, CONTENT_W);
      doc.text(headLines, MARGIN, cur);
      cur += headLines.length * 13 + 4;
      if (s.image) {
        try {
          doc.addImage(s.image, "PNG", MARGIN, cur, 180, 60);
          cur += 66;
        } catch (err) {
          console.warn("sig addImage failed:", err);
        }
      }
      return cur + 6;
    };
    // Page-break check — signatures must stay together.
    if (y + 160 > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
    y = drawSig(ticket.contractorSignature, "Contractor", y);
    y = drawSig(ticket.customerSignature, "Facility representative", y);

    if (recips.length) {
      y += 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Copies to:", MARGIN, y);
      doc.setFont("helvetica", "normal");
      const recipLines = doc.splitTextToSize(recips.join("; "), CONTENT_W - 70);
      doc.text(recipLines, MARGIN + 70, y);
    }

    const dateSlug =
      ticket.workDate || new Date().toISOString().slice(0, 10);
    const jobSlug = jobNumber || (job?.id || ticket.id || "").slice(0, 6);
    const filename = includeCosts
      ? `WorkOrder_Office_Job-${jobSlug}_${dateSlug}.pdf`
      : `WorkOrder_Job-${jobSlug}_${dateSlug}.pdf`;
    if (shouldShowPdfChooser()) {
      setPdfPending({ doc, filename });
    } else {
      await exportPdf(doc, filename);
    }
  };

  // --- Already signed: read-only record ---
  if (signed) {
    const sig = (s, label) =>
      s ? (
        <div style={{ marginTop: "6px" }}>
          <div style={{ fontSize: "12px" }}>
            <strong>{label}:</strong> {s.name}
            {s.title ? `, ${s.title}` : ""}
            {s.signedAt
              ? ` — ${new Date(s.signedAt).toLocaleString()}`
              : ""}
          </div>
          {s.image && (
            <img
              src={s.image}
              alt={`${label} signature`}
              style={{ border: "1px solid #ccc", borderRadius: "4px", maxWidth: "300px" }}
            />
          )}
        </div>
      ) : null;

    return (
      <div style={{ marginTop: "8px", paddingTop: "6px", borderTop: "1px dashed #ccc" }}>
        <strong style={{ color: "#1a7f37" }}>Signed &amp; locked</strong>
        {sig(ticket.contractorSignature, "Contractor")}
        {sig(ticket.customerSignature, "Facility representative")}
        {(ticket.sentTo || []).filter(Boolean).length > 0 && (
          <div style={{ fontSize: "12px", marginTop: "6px" }}>
            <strong>Copies to:</strong> {ticket.sentTo.filter(Boolean).join("; ")}
          </div>
        )}
        <div style={{ marginTop: "8px" }}>
          <button style={styles.button} onClick={() => printSignedCopy(false)}>
            Export Signed Copy
          </button>
          {canViewCosts && (
            <button
              style={{ ...styles.button, marginLeft: "6px" }}
              onClick={() => printSignedCopy(true)}
              title="Office record — includes material unit costs, totals, and source"
            >
              Export Itemized Copy
            </button>
          )}
        </div>
        <PdfActionModal
          pending={pdfPending}
          title="Export Signed Copy"
          onPrint={handlePdfPrint}
          onSave={handlePdfSave}
          onCancel={() => setPdfPending(null)}
        />
      </div>
    );
  }

  if (!canSign) return null;

  const ready =
    contractorName.trim() && contractorSig && custName.trim() && custSig;

  const applySignatures = async () => {
    if (!ready) return;
    const ok = window.confirm(
      "Apply both signatures and LOCK this ticket?\n\nOnce signed it can no longer be edited (kept for finance / records)."
    );
    if (!ok) return;
    setSaving(true);
    const now = new Date().toISOString();
    await actions.updateServiceTicket(ticket.id, {
      status: "signed",
      contractorSignature: {
        name: contractorName.trim(),
        title: "",
        image: contractorSig,
        signedAt: now,
      },
      customerSignature: {
        name: custName.trim(),
        title: custTitle.trim(),
        image: custSig,
        signedAt: now,
      },
      sentTo: recipients
        .split("\n")
        .map((r) => r.trim())
        .filter(Boolean),
      signedAt: now,
      updatedAt: now,
    });
    setSaving(false);
  };

  // Render as a fragment so the parent (JobServiceTickets) can lay the
  // Review&Sign button on the same flex row as Edit + Delete. The signed
  // branch above keeps its grouping div — it has more content to corral.
  return (
    <>
      <button style={styles.button} onClick={() => setOpen(true)}>
        Review &amp; Sign
      </button>

      {open && (
        // Full-screen overlay: when handed to the customer to sign, it covers
        // the whole app so they can't see the bill, costs, or other tickets.
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#fff",
            zIndex: 1000,
            overflowY: "auto",
            padding: "20px",
            maxWidth: "640px",
            margin: "0 auto",
            boxShadow: "0 0 0 100vw rgba(0,0,0,0.4)",
          }}
        >
          <h2 style={{ marginTop: 0 }}>Work Order — Review &amp; Sign</h2>
          {/* Customer-facing review — NO material cost shown */}
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "6px",
              padding: "8px",
              fontSize: "13px",
              background: "#fafafa",
            }}
          >
            <strong>Work Order Review</strong>
            <div style={{ marginTop: "4px" }}>
              <strong>Date:</strong> {ticket.workDate}
            </div>
            <div>
              <strong>Work done:</strong> {ticket.summary || "(no summary)"}
            </div>
            {eqNames.length > 0 && (
              <div>
                <strong>Equipment:</strong> {eqNames.join(", ")}
              </div>
            )}
            <div style={{ marginTop: "4px" }}>
              <strong>Labor:</strong>
              <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
                {(ticket.labor || []).map((l) => {
                  const ptLabel =
                    l.payType === "overtime"
                      ? "OT"
                      : l.payType === "double"
                      ? "DT"
                      : "";
                  return (
                  <li key={l.id}>
                    {l.employeeName || "—"} — {num(l.hours)} hr
                    {ptLabel && (
                      <span
                        style={{
                          fontSize: "10px",
                          color: "#b16500",
                          marginLeft: "4px",
                          fontWeight: "bold",
                        }}
                      >
                        {ptLabel}
                      </span>
                    )}
                    {l.date ? ` (${l.date})` : ""}
                  </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <strong>Materials:</strong>
              {(ticket.materials || []).length === 0 ? (
                <span style={{ color: "#666" }}> none</span>
              ) : (
                <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
                  {ticket.materials.map((m) => (
                    <li key={m.id}>
                      {m.name || "—"}
                      {m.partNumber ? ` (#${m.partNumber})` : ""} — qty {num(m.qty)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Signatures */}
          <div style={{ marginTop: "10px" }}>
            <strong style={{ fontSize: "13px" }}>Contractor signature</strong>
            <br />
            <input
              style={{ ...styles.input, width: "220px", margin: "2px 0" }}
              placeholder="Contractor name"
              value={contractorName}
              onChange={(e) => setContractorName(e.target.value)}
            />
            <SignatureInput onChange={setContractorSig} />
          </div>

          <div style={{ marginTop: "10px" }}>
            <strong style={{ fontSize: "13px" }}>
              Facility representative signature
            </strong>
            <br />
            <input
              style={{ ...styles.input, width: "220px", margin: "2px 0" }}
              placeholder="Representative name"
              value={custName}
              onChange={(e) => setCustName(e.target.value)}
            />
            <input
              style={{ ...styles.input, width: "220px", margin: "2px 0" }}
              placeholder="Title (e.g. Lead Engineer)"
              value={custTitle}
              onChange={(e) => setCustTitle(e.target.value)}
            />
            <SignatureInput onChange={setCustSig} />
          </div>

          {/* Copies to — recorded on the locked ticket; shown on the printed
              signed copy. One recipient per line. */}
          <div style={{ marginTop: "10px" }}>
            <strong style={{ fontSize: "13px" }}>
              Send signed copy to (one per line):
            </strong>
            <textarea
              style={styles.textarea}
              placeholder="Finance dept, Lead engineer, email addresses, etc. — one per line"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
            />
          </div>

          <div style={{ marginTop: "10px" }}>
            <button
              style={{
                ...styles.button,
                background: ready ? "#1a7f37" : "#999",
                color: "white",
              }}
              onClick={applySignatures}
              disabled={!ready || saving}
            >
              {saving ? "Saving..." : "Apply Signatures & Lock"}
            </button>
            <button style={styles.button} onClick={() => setOpen(false)}>
              Cancel
            </button>
            {!ready && (
              <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                Both names and both signatures are required.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
